import { Component } from '@theme/component';

/**
 * Accessible tabs for the "Explore our products" section.
 * Follows the WAI-ARIA tabs pattern: arrow keys, Home and End move between tabs,
 * and only the selected tab is in the tab order.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement[]} [tabs] - The tab buttons (absent with a single tab)
 * @property {HTMLElement[]} panels - The tab panels, in the same order as the tabs
 *
 * @extends {Component<Refs>}
 */
class ProductTabsComponent extends Component {
  requiredRefs = ['panels'];

  /**
   * @param {MouseEvent} event
   */
  selectTab(event) {
    const tab = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;
    if (!(tab instanceof HTMLButtonElement) || !this.refs.tabs) return;

    this.#activate(this.refs.tabs.indexOf(tab));
  }

  /**
   * @param {KeyboardEvent} event
   */
  handleKeydown(event) {
    const { tabs } = this.refs;
    if (!tabs?.length) return;
    const current = tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    const isRtl = getComputedStyle(this).direction === 'rtl';
    let next = -1;

    switch (event.key) {
      case 'ArrowRight':
        next = current + (isRtl ? -1 : 1);
        break;
      case 'ArrowLeft':
        next = current + (isRtl ? 1 : -1);
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    next = (next + tabs.length) % tabs.length;
    this.#activate(next);
    tabs[next]?.focus();
  }

  /**
   * @param {number} index
   */
  #activate(index) {
    const { tabs, panels } = this.refs;
    if (!tabs || index < 0 || index >= tabs.length) return;

    for (const [i, tab] of tabs.entries()) {
      const selected = i === index;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
      const panel = panels[i];
      if (panel) panel.hidden = !selected;
    }
  }
}

if (!customElements.get('ee-product-tabs')) {
  customElements.define('ee-product-tabs', ProductTabsComponent);
}
